import { useRef, useState } from 'react'
import {
  BarChart3, ChevronDown, ChevronRight, Cog, Database, FileText, FolderArchive, FolderOpen, GitBranch,
  Globe, GripVertical, HardDrive, Home, Layers, LayoutDashboard, Loader2, PanelTop, PieChart, Plus, Save,
  Search, StickyNote, Wand2, X,
} from 'lucide-react'
import type { FlatNavItem } from '@/types'
import type { PageKind } from '@/pages/designers/page/types'
import { PAGE_KIND_LABELS } from '@/pages/designers/page/types'
import type { ObjectStatus } from '@/api/journey'
import vocabulary from '@/pages/workbench/simple/vocabulary.json'
import '@/pages/workbench/simple/simple.css'

/**
 * The app's menu tree — ONE component (moved out of the Preview tab with its
 * drag logic, no behaviour change):
 *
 *   • drag-and-drop before / inside / after, auto-expand on hover, and the
 *     container rules (only folders `-` and websites `X` take children);
 *   • the explicit Save of the menu order (a drop only edits the in-memory
 *     order; nothing is written until Save);
 *   • search (matches keep their ancestors and descendants visible);
 *   • the INT / EXT / DOC badge on Smart Pages and the landing-page home icon;
 *   • a website's header/footer pages and collection templates folded away.
 *
 * New look: a white panel, the type's icon, a second line "Transaction ·
 * Ready to check" and ONE status dot (planned / building / ready to check /
 * checked) in place of the two spec/model circles.
 */

export const TYPE_ICON: Record<string, typeof FolderOpen> = {
  '-': FolderOpen,
  T: FileText,
  D: LayoutDashboard,
  V: PieChart,
  M: Database,
  R: BarChart3,
  G: Cog,
  W: GitBranch,
  P: HardDrive,
  S: StickyNote,
  X: Globe,
  K: FolderArchive,
  U: Wand2,
}

export function typeName(objType: string): string {
  return (vocabulary.typeNames as Record<string, string>)[objType] || objType
}

export function statusLabel(status: ObjectStatus | null | undefined): string {
  return status ? (vocabulary.status as Record<string, string>)[status] || status : ''
}

export function getChildren(nav: FlatNavItem[], parentId: string): FlatNavItem[] {
  return nav
    .filter((n) => n.parent_id === parentId)
    .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
}

/**
 * Display grouping for a website (X) node's children. The main list keeps the
 * STORED order (`order`), so drag-and-drop arranges pages and folders exactly
 * as dropped; the landing page is only marked (home icon), never moved. The
 * header/footer/pop-up pages and the collection TEMPLATES (one page per
 * record) are gathered into two display-only groups at the bottom. Nothing
 * is written — stored `order` and `parent_id` are untouched.
 */
interface ArrangedSite {
  main: FlatNavItem[]
  homeId: string | null
  chrome: FlatNavItem[]
  templates: FlatNavItem[]
}
export function arrangeSiteChildren(site: FlatNavItem, children: FlatNavItem[]): ArrangedSite {
  let spec: Record<string, unknown> = {}
  try {
    const v = JSON.parse(site.functional_specs || '{}')
    if (v && typeof v === 'object') spec = v
  } catch { /* free-text legacy spec — no site settings to read */ }
  const overrides = (spec.page_overrides && typeof spec.page_overrides === 'object'
    ? spec.page_overrides : {}) as Record<string, { is_home?: boolean; collection?: { role?: string } }>
  const chromeNames = new Set(
    [spec.header_page_obj_name, spec.footer_page_obj_name, spec.popup_page_obj_name]
      .filter((v): v is string => typeof v === 'string' && !!v),
  )
  const isTemplate = (n: FlatNavItem) => {
    const role = overrides[n.obj_name]?.collection?.role
    return role === 'item' || role === 'taxonomy'
  }
  const chrome = children.filter((c) => c.obj_type === 'S' && chromeNames.has(c.obj_name))
  const templates = children.filter((c) => c.obj_type === 'S' && !chromeNames.has(c.obj_name) && isTemplate(c))
  const main = children.filter((c) => !chrome.includes(c) && !templates.includes(c))
  const pages = main.filter((c) => c.obj_type === 'S')
  const explicit = typeof spec.home_page_obj_name === 'string' ? spec.home_page_obj_name : ''
  const home = pages.find((p) => p.obj_name === explicit)
    || pages.find((p) => overrides[p.obj_name]?.is_home === true)
    || pages[0]
  return { main, homeId: home?.id ?? null, chrome, templates }
}

/**
 * Ids of nav items to keep visible for a search query: every item whose title
 * or object name matches, plus all of its ancestors (so the path stays
 * visible) and all of its descendants. Returns an empty set when nothing
 * matches; callers treat a `null` filter as "show everything".
 */
export function collectNavMatches(nav: FlatNavItem[], query: string): Set<string> {
  const show = new Set<string>()
  const q = query.trim().toLowerCase()
  if (!q) return show
  const byId = new Map(nav.map((n) => [n.id, n]))
  const addAncestors = (item: FlatNavItem) => {
    let cur: FlatNavItem | undefined = item
    const seen = new Set<string>()
    while (cur && cur.parent_id && !seen.has(cur.id)) {
      seen.add(cur.id)
      const parent = byId.get(cur.parent_id)
      if (!parent) break
      show.add(parent.id)
      cur = parent
    }
  }
  const addDescendants = (id: string) => {
    for (const child of nav.filter((n) => n.parent_id === id)) {
      show.add(child.id)
      addDescendants(child.id)
    }
  }
  for (const item of nav) {
    const hay = `${item.title || ''} ${item.obj_name || ''}`.toLowerCase()
    if (hay.includes(q)) {
      show.add(item.id)
      addAncestors(item)
      addDescendants(item.id)
    }
  }
  return show
}

/**
 * Ids visible under an object-type filter (Pages, Dashboards…): every object
 * of one of `types`, plus the folders/websites that lead to it. `null` = no
 * filter. Containers listed in `types` are shown only when they hold
 * something visible (or nothing at all, so an empty folder can be filled).
 * `keep` = ids shown whatever their type (added on this tab, not saved yet —
 * an object must not vanish from under the person who just added it).
 */
export function collectTypeMatches(
  nav: FlatNavItem[], types: string[] | null | undefined, keep?: ReadonlySet<string>,
): Set<string> | null {
  if (!types || !types.length) return null
  const want = new Set(types)
  const byId = new Map(nav.map((n) => [n.id, n]))
  const show = new Set<string>()
  for (const item of nav) {
    const kept = !!keep?.has(item.id)
    if (!kept && (item.obj_type === '-' || !want.has(item.obj_type))) continue
    show.add(item.id)
    let cur: FlatNavItem | undefined = item
    const seen = new Set<string>()
    while (cur && cur.parent_id && !seen.has(cur.id)) {
      seen.add(cur.id)
      const parent = byId.get(cur.parent_id)
      if (!parent) break
      show.add(parent.id)
      cur = parent
    }
  }
  if (want.has('-')) {
    for (const f of nav) {
      if (f.obj_type === '-' && !nav.some((n) => n.parent_id === f.id)) show.add(f.id)
    }
  }
  return show
}

export function isDescendant(nav: FlatNavItem[], nodeId: string, potentialAncestorId: string): boolean {
  let current = nav.find((n) => n.id === nodeId)
  const seen = new Set<string>()
  while (current && current.parent_id) {
    if (current.parent_id === potentialAncestorId) return true
    if (seen.has(current.id)) return false
    seen.add(current.id)
    current = nav.find((n) => n.id === current!.parent_id)
  }
  return false
}

/** The new navigation after dropping `dragId` on `target` (before/inside/after). */
export function moveNavItem(
  navigation: FlatNavItem[], dragId: string, target: FlatNavItem, position: 'before' | 'after' | 'inside',
): FlatNavItem[] | null {
  if (dragId === target.id) return null
  if (isDescendant(navigation, target.id, dragId)) return null
  const nav = navigation.map((n) => ({ ...n }))
  const dragNode = nav.find((n) => n.id === dragId)
  if (!dragNode) return null
  if (position === 'inside') {
    dragNode.parent_id = target.id
    const siblings = nav
      .filter((n) => n.parent_id === target.id)
      .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
    const withoutDrag = siblings.filter((n) => n.id !== dragId)
    withoutDrag.push(dragNode)
    withoutDrag.forEach((n, i) => { n.order = i })
  } else {
    const newParentId = target.parent_id
    dragNode.parent_id = newParentId
    const siblings = nav
      .filter((n) => n.id !== dragId && n.parent_id === newParentId)
      .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
    const targetIdx = siblings.findIndex((n) => n.id === target.id)
    const insertIdx = position === 'before' ? targetIdx : targetIdx + 1
    siblings.splice(insertIdx, 0, dragNode)
    siblings.forEach((n, i) => { n.order = i })
  }
  return nav
}

/* ── NavTreeItem ─────────────────────────────────────── */

interface NavTreeItemProps {
  item: FlatNavItem
  navigation: FlatNavItem[]
  selectedId: string | null
  draggedId: string | null
  onSelect: (item: FlatNavItem) => void
  onDragStart: (e: React.DragEvent, item: FlatNavItem) => void
  onDrop: (e: React.DragEvent, targetItem: FlatNavItem, position: 'before' | 'after' | 'inside') => void
  onDragEnd: () => void
  expandedIds: Set<string>
  toggleExpand: (id: string) => void
  onAutoExpand: (id: string) => void
  statusOf: (item: FlatNavItem) => ObjectStatus | null
  ancestorIds?: Set<string>
  /** Active search: only these render, and their containers open. */
  filterIds?: Set<string> | null
  /** Type filter (Pages / Dashboards tab): only these render. */
  visibleIds?: Set<string> | null
  isHome?: boolean
}

function NavTreeItem({
  item, navigation, selectedId, draggedId, onSelect, onDragStart, onDrop, onDragEnd,
  expandedIds, toggleExpand, onAutoExpand, statusOf, ancestorIds, filterIds, visibleIds, isHome = false,
}: NavTreeItemProps) {
  const [dropPosition, setDropPosition] = useState<'before' | 'after' | 'inside' | null>(null)
  const rowRef = useRef<HTMLDivElement>(null)
  const autoExpandTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const ownAncestors = ancestorIds ?? new Set<string>()
  const children = getChildren(navigation, item.id).filter((c) => !ownAncestors.has(c.id) && c.id !== item.id)
  const childAncestors = new Set([...ownAncestors, item.id])
  const isExpanded = expandedIds.has(item.id)
  const isSelected = selectedId === item.id
  const hasChildren = children.length > 0
  const isFolder = item.obj_type === '-'
  // Container = node that meaningfully holds children. Folders are pure
  // containers; External Website (X) is also a container — its child Smart
  // Pages are the website's pages. Drop / expand behaviour keys on this.
  const isContainer = isFolder || item.obj_type === 'X'
  const Icon = TYPE_ICON[item.obj_type] || FileText
  const isDragged = draggedId === item.id
  const isDragDescendant = draggedId ? isDescendant(navigation, item.id, draggedId) : false
  const status = isFolder ? null : statusOf(item)

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    if (isDragged || isDragDescendant) {
      e.dataTransfer.dropEffect = 'none'
      setDropPosition(null)
      return
    }
    e.dataTransfer.dropEffect = 'move'
    const rect = rowRef.current?.getBoundingClientRect()
    if (!rect) return
    const y = e.clientY - rect.top
    const height = rect.height
    // Containers: top 25% = before, middle 50% = inside, bottom 25% = after.
    // Non-containers split 50/50 between before / after.
    if (isContainer) {
      if (y < height * 0.25) {
        setDropPosition('before')
      } else if (y > height * 0.75) {
        setDropPosition('after')
      } else {
        setDropPosition('inside')
        // Auto-expand collapsed containers after hovering 600ms
        if (!isExpanded && !autoExpandTimer.current) {
          autoExpandTimer.current = setTimeout(() => {
            onAutoExpand(item.id)
            autoExpandTimer.current = null
          }, 600)
        }
      }
    } else {
      setDropPosition(y < height / 2 ? 'before' : 'after')
    }
    if (dropPosition !== 'inside' && autoExpandTimer.current) {
      clearTimeout(autoExpandTimer.current)
      autoExpandTimer.current = null
    }
  }

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    if (autoExpandTimer.current) {
      clearTimeout(autoExpandTimer.current)
      autoExpandTimer.current = null
    }
    if (dropPosition && !isDragged && !isDragDescendant) {
      onDrop(e, item, dropPosition)
    }
    setDropPosition(null)
  }

  const handleDragLeave = (e: React.DragEvent) => {
    if (rowRef.current && !rowRef.current.contains(e.relatedTarget as Node)) {
      setDropPosition(null)
      if (autoExpandTimer.current) {
        clearTimeout(autoExpandTimer.current)
        autoExpandTimer.current = null
      }
    }
  }

  if (filterIds && !filterIds.has(item.id)) return null
  if (visibleIds && !visibleIds.has(item.id)) return null

  const insideHighlight = dropPosition === 'inside' && !isDragged && !isDragDescendant
  const dropLine = (where: 'top' | 'bottom') => (
    <div style={{
      position: 'absolute', [where]: 0, left: 8, right: 8, height: 2,
      background: 'var(--color-accent)', borderRadius: 1, zIndex: 2,
    }}>
      <div style={{ position: 'absolute', left: -3, top: -3, width: 8, height: 8, borderRadius: '50%', background: 'var(--color-accent)' }} />
    </div>
  )

  const kindBadge = item.obj_type === 'S' ? (() => {
    const k = (item.page_kind === 'external' || item.page_kind === 'document') ? item.page_kind : 'internal'
    return (
      <span
        title={PAGE_KIND_LABELS[k as PageKind]}
        style={{
          flexShrink: 0, fontSize: 13, fontWeight: 700, letterSpacing: '0.06em', padding: '0 6px',
          borderRadius: 4, background: 'var(--color-surface-2)', color: 'var(--color-text-secondary)',
        }}
      >
        {(vocabulary.pageKindBadge as Record<string, string>)[k] || k}
      </span>
    )
  })() : null

  return (
    <div style={{ position: 'relative' }}>
      {dropPosition === 'before' && !isDragged && !isDragDescendant && dropLine('top')}
      <div
        ref={rowRef}
        draggable
        role="treeitem"
        aria-selected={isSelected}
        aria-expanded={isContainer ? isExpanded : undefined}
        tabIndex={0}
        onDragStart={(e) => onDragStart(e, item)}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
        onDragLeave={handleDragLeave}
        onDragEnd={onDragEnd}
        onClick={(e) => { e.stopPropagation(); onSelect(item) }}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onSelect(item) }
          if (isContainer && e.key === 'ArrowRight' && !isExpanded) toggleExpand(item.id)
          if (isContainer && e.key === 'ArrowLeft' && isExpanded) toggleExpand(item.id)
        }}
        data-tw-nav={item.id}
        style={{
          display: 'flex', alignItems: 'center', gap: 6, minHeight: isFolder ? 38 : 44,
          padding: '4px 8px', borderRadius: 8, cursor: 'pointer', userSelect: 'none',
          color: 'var(--color-text)',
          background: isSelected ? 'var(--color-accent-weak)' : 'transparent',
          boxShadow: insideHighlight ? 'inset 0 0 0 2px var(--color-accent)' : isSelected ? 'inset 3px 0 0 var(--color-accent)' : 'none',
          opacity: isDragged ? 0.4 : 1,
          transition: 'background 150ms, opacity 150ms',
          position: 'relative',
        }}
        onMouseEnter={(e) => { if (!isSelected) e.currentTarget.style.background = 'var(--color-surface-2)' }}
        onMouseLeave={(e) => { if (!isSelected) e.currentTarget.style.background = 'transparent' }}
      >
        <GripVertical size={12} aria-hidden="true" style={{ color: 'var(--color-text-faint)', flexShrink: 0, cursor: 'grab' }} />
        {(hasChildren || isContainer) ? (
          <button
            type="button"
            aria-label={isExpanded ? `Collapse ${item.title}` : `Expand ${item.title}`}
            onClick={(e) => { e.stopPropagation(); toggleExpand(item.id) }}
            style={{ display: 'flex', alignItems: 'center', color: 'var(--color-text-secondary)', flexShrink: 0, background: 'none', border: 0, padding: 0, cursor: 'pointer' }}
          >
            {isExpanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
          </button>
        ) : (
          <span style={{ width: 14, flexShrink: 0 }} />
        )}
        {isHome
          ? <span title="Landing page"><Home size={17} style={{ color: 'var(--color-accent)', flexShrink: 0 }} /></span>
          : <Icon size={17} style={{ color: isFolder ? 'var(--color-text-secondary)' : 'var(--color-accent)', flexShrink: 0 }} />}
        <span style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
          <span style={{ fontSize: 14, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {item.title || item.obj_name || '(untitled)'}
          </span>
          {!isFolder && (
            <span style={{ fontSize: 13, color: 'var(--color-text-secondary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {typeName(item.obj_type)}{status ? ` · ${statusLabel(status)}` : ''}
            </span>
          )}
        </span>
        {isFolder && hasChildren && (
          <span style={{ fontSize: 13, color: 'var(--color-text-secondary)' }}>{children.length}</span>
        )}
        {kindBadge}
        {status && <span title={statusLabel(status)} className={`swb-dot swb-dot-${status}`} />}
      </div>
      {dropPosition === 'after' && !isDragged && !isDragDescendant && dropLine('bottom')}
      {(isExpanded || filterIds) && hasChildren && (() => {
        const renderChild = (child: FlatNavItem, home = false) => (
          <NavTreeItem
            key={child.id}
            item={child}
            navigation={navigation}
            selectedId={selectedId}
            draggedId={draggedId}
            onSelect={onSelect}
            onDragStart={onDragStart}
            onDrop={onDrop}
            onDragEnd={onDragEnd}
            expandedIds={expandedIds}
            toggleExpand={toggleExpand}
            onAutoExpand={onAutoExpand}
            statusOf={statusOf}
            ancestorIds={childAncestors}
            filterIds={filterIds}
            visibleIds={visibleIds}
            isHome={home}
          />
        )
        if (item.obj_type !== 'X') {
          return <div role="group" style={{ marginLeft: '1.1rem' }}>{children.map((c) => renderChild(c))}</div>
        }
        // A website keeps its stored (drag-and-drop) order, landing page
        // marked; its header/footer pages and collection templates fold away below.
        const arranged = arrangeSiteChildren(item, children)
        const group = (key: string, label: string, GIcon: typeof Home, list: FlatNavItem[], hint: string) => {
          if (!list.length) return null
          const gid = `${item.id}::${key}`
          const visible = filterIds ? list.filter((c) => filterIds.has(c.id)) : list
          if (filterIds && !visible.length) return null
          const open = !!filterIds || expandedIds.has(gid)
          return (
            <div key={gid}>
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); toggleExpand(gid) }}
                title={hint}
                aria-expanded={open}
                style={{
                  display: 'flex', alignItems: 'center', gap: 6, padding: '6px 8px', width: '100%',
                  fontSize: 13, color: 'var(--color-text-secondary)', cursor: 'pointer', userSelect: 'none',
                  background: 'none', border: 0, textAlign: 'left', fontFamily: 'inherit',
                }}
              >
                <span style={{ width: 12, flexShrink: 0 }} />
                {open ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                <GIcon size={14} style={{ flexShrink: 0 }} />
                <span style={{ fontStyle: 'italic' }}>{label}</span>
                <span style={{ marginLeft: 'auto' }}>{list.length}</span>
              </button>
              {open && <div role="group" style={{ marginLeft: '1.1rem' }}>{visible.map((c) => renderChild(c))}</div>}
            </div>
          )
        }
        return (
          <div role="group" style={{ marginLeft: '1.1rem' }}>
            {arranged.main.map((c) => renderChild(c, c.id === arranged.homeId))}
            {group('chrome', 'Header & footer', PanelTop, arranged.chrome,
              'The pages drawn around every page of the site (set in the website settings).')}
            {group('templates', 'Templates', Layers, arranged.templates,
              'Collection templates — the shape of one page per record (a post, a category).')}
          </div>
        )
      })()}
    </div>
  )
}

/* ── AppTree (panel) ─────────────────────────────────── */

export interface AppTreeProps {
  navigation: FlatNavItem[]
  selectedId: string | null
  /** Called for a click on a row; the caller applies its unsaved-changes guard. */
  onSelect: (item: FlatNavItem) => void
  /** Clicking the panel heading clears the selection. */
  onDeselect?: () => void
  expandedIds: Set<string>
  onToggle: (id: string) => void
  onExpand: (id: string) => void
  /** A drop produced a new order — in memory only, until Save. */
  onMove: (navigation: FlatNavItem[]) => void
  statusOf: (item: FlatNavItem) => ObjectStatus | null
  /** Object types this tab shows (Pages, Dashboards…); null = all. */
  types?: string[] | null
  /** Ids shown whatever `types` says (added here, not saved yet). */
  keepIds?: ReadonlySet<string>
  heading: string
  nouns: string
  addLabel: string
  onAdd: () => void
  onSave: () => void
  saving: boolean
  /** The order changed since the last save. */
  dirty?: boolean
  scrollRef?: React.Ref<HTMLDivElement>
}

export default function AppTree({
  navigation, selectedId, onSelect, onDeselect, expandedIds, onToggle, onExpand, onMove, statusOf, types, keepIds,
  heading, nouns, addLabel, onAdd, onSave, saving, dirty, scrollRef,
}: AppTreeProps) {
  const [query, setQuery] = useState('')
  const dragItemRef = useRef<FlatNavItem | null>(null)
  const [draggedId, setDraggedId] = useState<string | null>(null)

  const handleDragStart = (e: React.DragEvent, item: FlatNavItem) => {
    dragItemRef.current = item
    setDraggedId(item.id)
    // Required for Firefox and some browsers to initiate drag
    e.dataTransfer.setData('text/plain', item.id)
    e.dataTransfer.effectAllowed = 'move'
  }
  const handleDragEnd = () => {
    dragItemRef.current = null
    setDraggedId(null)
  }
  const handleDrop = (_e: React.DragEvent, target: FlatNavItem, position: 'before' | 'after' | 'inside') => {
    const drag = dragItemRef.current
    dragItemRef.current = null
    setDraggedId(null)
    if (!drag) return
    const next = moveNavItem(navigation, drag.id, target, position)
    if (next) onMove(next)
  }

  const rootItems = getChildren(navigation, '')
  const filterIds = query.trim() ? collectNavMatches(navigation, query) : null
  const visibleIds = collectTypeMatches(navigation, types, keepIds)
  const countable = navigation.filter((n) => n.obj_type !== '-' && (!visibleIds || visibleIds.has(n.id)))
  const shownRoots = rootItems.filter((r) => !visibleIds || visibleIds.has(r.id))

  return (
    <nav
      aria-label={heading}
      className="swb-card"
      style={{ display: 'flex', flexDirection: 'column', minHeight: 0, height: '100%', overflow: 'hidden' }}
    >
      <div style={{ padding: '12px 12px 8px', display: 'flex', flexDirection: 'column', gap: 10, flexShrink: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, padding: '0 4px' }}>
          <button
            type="button"
            onClick={onDeselect}
            style={{ background: 'none', border: 0, padding: 0, fontWeight: 600, fontSize: 15, color: 'var(--color-text)', cursor: onDeselect ? 'pointer' : 'default', fontFamily: 'inherit' }}
          >
            {heading}
          </button>
          <span style={{ fontSize: 13, color: 'var(--color-text-secondary)' }}>
            {countable.length} {nouns}
          </span>
        </div>
        <div style={{ position: 'relative' }}>
          <Search size={14} aria-hidden="true" style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: 'var(--color-text-faint)' }} />
          <label htmlFor="app-tree-search" style={{ position: 'absolute', width: 1, height: 1, overflow: 'hidden', clip: 'rect(0 0 0 0)' }}>Find</label>
          <input
            id="app-tree-search"
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Escape') setQuery('') }}
            placeholder={`Search ${nouns}`}
            style={{ width: '100%', boxSizing: 'border-box', minHeight: 36, padding: '0 30px 0 32px', borderRadius: 8, fontSize: 14 }}
          />
          {query && (
            <button type="button" aria-label="Clear search" onClick={() => setQuery('')}
              style={{ position: 'absolute', right: 6, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 0, cursor: 'pointer', color: 'var(--color-text-secondary)', display: 'flex' }}>
              <X size={14} />
            </button>
          )}
        </div>
        <div style={{ display: 'flex', gap: 6 }}>
          <button type="button" className="swb-btn swb-btn-secondary swb-btn-sm" style={{ flex: 1 }} onClick={onAdd} data-tw-action="add-screen">
            <Plus size={14} /> {addLabel}
          </button>
          <button
            type="button"
            className={`swb-btn swb-btn-sm ${dirty ? 'swb-btn-primary' : 'swb-btn-secondary'}`}
            onClick={onSave}
            disabled={saving}
            title={dirty ? 'Save the new menu order' : 'Save the menu'}
            data-tw-action="save-menu"
          >
            {saving ? <Loader2 size={14} style={{ animation: 'spin 1s linear infinite' }} /> : <Save size={14} />}
            Save
          </button>
        </div>
      </div>
      <div ref={scrollRef} role="tree" style={{ flex: 1, overflow: 'auto', padding: '0 6px 8px' }}>
        {shownRoots.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '2rem 1rem', color: 'var(--color-text-secondary)', fontSize: 14 }}>
            Nothing here yet. Use “{addLabel}” or ask the AI to build it.
          </div>
        ) : filterIds && filterIds.size === 0 ? (
          <div style={{ textAlign: 'center', padding: '2rem 1rem', color: 'var(--color-text-secondary)', fontSize: 14 }}>
            Nothing matches “{query}”.
          </div>
        ) : (
          shownRoots.map((item) => (
            <NavTreeItem
              key={item.id}
              item={item}
              navigation={navigation}
              selectedId={selectedId}
              draggedId={draggedId}
              onSelect={onSelect}
              onDragStart={handleDragStart}
              onDrop={handleDrop}
              onDragEnd={handleDragEnd}
              expandedIds={expandedIds}
              toggleExpand={onToggle}
              onAutoExpand={onExpand}
              statusOf={statusOf}
              filterIds={filterIds}
              visibleIds={visibleIds}
            />
          ))
        )}
      </div>
    </nav>
  )
}
